// src/web/client/src/utils/authenticatedFetch.js
// Global fetch wrapper that adds session token automatically
// Uses XMLHttpRequest as fallback when fetch is blocked by Shopify

import { getShopifySessionToken } from './shopifyAuth';

// XHR-based fetch that bypasses Shopify's fetch override
const xhrFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';

    xhr.open(method, url);

    // Bound every request so a stuck/dead backend can't hang the UI forever.
    // ontimeout below rejects the promise so callers can fail gracefully.
    xhr.timeout = options.timeout || 20000;

    // Set headers
    const headers = options.headers || {};
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onload = () => {
      // Copy EVERY response header the browser exposes — not just Content-Type.
      // The old Content-Type-only rebuild silently dropped custom headers
      // (X-Total-Count et al.), breaking the Messages-screen pagination.
      // getAllResponseHeaders() returns CRLF-separated "name: value" lines.
      const responseHeaders = new Headers();
      xhr.getAllResponseHeaders().trim().split(/[\r\n]+/).forEach((line) => {
        const idx = line.indexOf(': ');
        if (idx > 0) responseHeaders.append(line.slice(0, idx), line.slice(idx + 2));
      });
      // Preserve the old default for a headerless response — json() callers
      // (the 402 path, Dashboard's content-type check) behaved on this.
      if (!responseHeaders.has('Content-Type')) {
        responseHeaders.set('Content-Type', 'application/json');
      }
      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: responseHeaders,
      });
      resolve(response);
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error'));
    };
    
    xhr.ontimeout = () => {
      reject(new Error('Request timeout'));
    };
    
    // Send body for POST/PUT requests
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

// Error class thrown when the backend says the shop needs a plan / has a
// cancelled or expired subscription. UI code can `instanceof SubscriptionRequiredError`
// to render a friendly "choose a plan" CTA instead of an "Error Loading…"
// banner. Carries the backend's status code + message so the screen can
// say the right thing (pending vs expired vs cancelled).
export class SubscriptionRequiredError extends Error {
  constructor(payload = {}) {
    super(payload.message || 'A subscription is required to use this feature.');
    this.name = 'SubscriptionRequiredError';
    this.code = payload.code || 'SUBSCRIPTION_REQUIRED';
    this.subscriptionStatus = payload.subscription_status || null;
    this.upgradeUrl = payload.upgrade_url || null;
    this.plans = payload.plans || null;
  }
}

// _retriedOnce is PRIVATE (public signature unchanged): it caps the retry
// below at exactly one — max two sends per call, a loop is impossible by
// construction.
export const authenticatedFetch = async (url, options = {}, _retriedOnce = false) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  try {
    const sessionToken = await getShopifySessionToken();
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
  } catch (error) {
    console.warn('[AuthFetch] Could not get session token:', error);
  }

  // Use XHR-based fetch to bypass Shopify's fetch override
  const response = await xhrFetch(url, {
    ...options,
    headers
  });

  // Retry ONCE on the B2 session-token 401: a server deploy/restart can
  // expire a 60-second token mid-flight (the request waited out the boot),
  // and the very next idToken() call mints a fresh one — the client token
  // path never caches (shopifyAuth pins this). The 401 itself proves the
  // server is awake again, so the single recursion re-runs the attach block
  // verbatim with a fresh token and lands on a warm server. Only the B2
  // code retries — SESSION_TOKEN_REQUIRED is unique to verifySessionToken;
  // billing/legacy 401s (no such code) and 403 SHOP_MISMATCH never match.
  // Replay safety: every call site sends a JSON.stringify string body
  // (audited); the one FormData upload uses its own xhrUpload, not this
  // wrapper.
  if (response.status === 401 && !_retriedOnce) {
    let code = null;
    try {
      code = (await response.clone().json()).code;
    } catch (_) {
      /* non-JSON 401 body — not the B2 signal, do not retry */
    }
    if (code === 'SESSION_TOKEN_REQUIRED') {
      return authenticatedFetch(url, options, true);
    }
  }

  // 402 = backend says the shop has no active subscription. Dispatch a
  // global event so the SubscriptionGuard can render a consistent
  // "choose a plan" overlay on EVERY screen (not just the ones that
  // happen to catch this typed error inline), then throw so the calling
  // screen can still bail out of its load flow gracefully.
  if (response.status === 402) {
    let payload = {};
    try {
      const cloned = response.clone ? response.clone() : response;
      payload = await cloned.json();
    } catch (_) {
      /* non-JSON 402 — fall back to defaults */
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscription:required', {
        detail: {
          code: payload.code,
          message: payload.message,
          subscriptionStatus: payload.subscription_status,
          upgradeUrl: payload.upgrade_url || '/billing'
        }
      }));
    }
    throw new SubscriptionRequiredError(payload);
  }

  return response;
};

export default authenticatedFetch;